// src/components/checkout/LayoutUploader/LayoutUploader.jsx
//
// ПРИЁМ МАКЕТА НА ОФОРМЛЕНИИ ЗАКАЗА (этапы 1.1–1.3 SHOP_PLAN.txt, сессия 9).
//
// ═══════════════════════════════════════════════════════════════════════
// ЧТО ЭТОТ БЛОК ДЕЛАЕТ
//
// Для КАЖДОЙ позиции заказа клиент выбирает одно из ДВУХ:
//   • приложить файл макета (PDF, AI, EPS, CDR, PSD, JPG, PNG, TIFF, ZIP, WebP);
//   • поставить галочку «Макета нет, пришлю позже» → `layout_status='pending'`.
//
// 🔴 ПОЧЕМУ НА КАЖДУЮ ПОЗИЦИЮ, А НЕ ОДИН НА ЗАКАЗ.
//    Так устроена БД: макет лежит в `order_items.layout_file`, и там же
//    записан замысел — «у разных позиций разные файлы (баннер — один,
//    вывеска — другой)» (`orderController.js`). Один файл на весь заказ
//    заставил бы менять схему и потерял бы это различие.
//
// 🔑 ПОЧЕМУ ЗАГРУЗКА СРАЗУ ПРИ ВЫБОРЕ ФАЙЛА, А НЕ ПРИ ОТПРАВКЕ ФОРМЫ.
//    Файл до 20 МБ. Если грузить его вместе с заказом, то ошибка в любом
//    поле формы заставит клиента заливать 20 МБ ПОВТОРНО. Здесь файл
//    уезжает сразу, а заказ лишь ССЫЛАЕТСЯ на готовый снимок.
//
// ⚠️ ЧЕСТНОСТЬ СОСТОЯНИЙ. Блок обязан различать:
//   • файл ещё НЕ выбран — обычное состояние;
//   • идёт загрузка — прогресс в процентах (не «пожалуйста, подождите»);
//   • файл ЗАГРУЖЕН — имя, размер, кнопка «удалить»;
//   • загрузка НЕ УДАЛАСЬ — текст ошибки от сервера, и клиент может
//     попробовать снова. Молчаливая неудача здесь стоила бы заказа:
//     клиент думал бы, что макет принят.
// ═══════════════════════════════════════════════════════════════════════

import React, { useRef, useState } from 'react';
import { uploadsAPI } from '../../../api/uploads.js';

// 🔑 ТЕ ЖЕ 10 ФОРМАТОВ, ЧТО ПРИНИМАЕТ СЕРВЕР (`middleware/upload.js`).
//    Список здесь — только подсказка в атрибуте `accept`, он НЕ защита:
//    браузер его игнорирует при перетаскивании, а злоумышленник обойдёт.
//    Настоящая проверка — на сервере (белый список + магические байты).
const ACCEPT = '.pdf,.ai,.eps,.cdr,.psd,.jpg,.jpeg,.png,.tiff,.tif,.zip,.webp';

// 🔴 ЛИМИТ РАЗМЕРА — КОПИЯ СЕРВЕРНОГО, И ЭТО ОСОЗНАННОЕ ДУБЛИРОВАНИЕ.
//    Источник истины — `MAX_LAYOUT_SIZE` в `backend/src/middleware/upload.js`
//    (сейчас 20 МБ, решение владельца). Здесь он нужен, чтобы НЕ ГОНЯТЬ
//    20 МБ по мобильному интернету ради отказа в конце загрузки.
//
//    ⚠️ РАСХОЖДЕНИЕ ЗДЕСЬ БЕЗОПАСНО В ОДНУ СТОРОНУ: если сервер опустит
//       лимит ниже, клиент узнает об этом от сервера (413) — то есть
//       проверка на фронте только ЭКОНОМИТ трафик, но не заменяет сервер.
//       Обратное расхождение (фронт строже сервера) отсекало бы валидные
//       файлы, поэтому при смене лимита на сервере правь и эту константу.
const MAX_SIZE = 20 * 1024 * 1024; // 20 МБ

// Расширения, которые реально принимает сервер. Проверяем ИМЯ, а не
// `file.type`: для `.cdr`/`.ai`/`.psd` браузер часто отдаёт пустую строку
// или `application/octet-stream`, и проверка по MIME отсекала бы валидные
// макеты. Содержимое всё равно проверит сервер по магическим байтам.
const ALLOWED_EXT = [
  '.pdf', '.ai', '.eps', '.cdr', '.psd', '.jpg', '.jpeg', '.png',
  '.tiff', '.tif', '.zip', '.webp',
];

/** Человекочитаемый размер. Байты клиенту ничего не говорят. */
const formatSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
};

/**
 * @param {object} props
 * @param {object|null} props.layout — снимок макета или `null`.
 * @param {(snapshot: object|null) => void} props.onChange — сообщить
 *        родителю новый снимок (`null` — макета нет).
 * @param {boolean} props.pending — стоит ли галочка «пришлю позже».
 * @param {(pending: boolean) => void} props.onPendingChange
 */
export const LayoutUploader = ({ layout, onChange, pending, onPendingChange }) => {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const handleFile = async (file) => {
    if (!file) return;
    setError('');

    // ── ПРОВЕРКИ ДО ОТПРАВКИ ──────────────────────────────────────────
    // 🔴 ЗАЧЕМ: без них клиент на мобильном интернете тратит минуты на
    //    отправку 20 МБ и получает отказ в самом конце. Лимит снижали до
    //    20 МБ именно ради мобильных — значит, проверка обязана быть ДО
    //    загрузки, а не после.
    // ⚠️ Это НЕ замена серверных проверок: фронт можно обойти. Цель —
    //    сэкономить время клиента, а не защитить сервер.

    // 1. Размер.
    if (file.size > MAX_SIZE) {
      setError(
        `Файл слишком большой: ${formatSize(file.size)}. `
        + `Максимальный размер — ${formatSize(MAX_SIZE)}. `
        + 'Если макет тяжелее, пришлите ссылку на файл или архив '
        + 'и отметьте «пришлю позже» — мы свяжемся.'
      );
      // Сбрасываем input: без этого повторный выбор ТОГО ЖЕ файла не
      // вызовет onChange, и клиент решит, что кнопка не работает.
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    // 2. Расширение.
    const dot = file.name.lastIndexOf('.');
    const ext = dot >= 0 ? file.name.slice(dot).toLowerCase() : '';
    if (!ALLOWED_EXT.includes(ext)) {
      setError(
        ext
          ? `Формат ${ext} не принимается. Нужен макет: PDF, AI, EPS, CDR, `
            + 'PSD, JPG, PNG, TIFF, ZIP или WebP.'
          : 'У файла нет расширения — не можем определить формат. '
            + 'Нужен макет: PDF, AI, EPS, CDR, PSD, JPG, PNG, TIFF, ZIP, WebP.'
      );
      if (inputRef.current) inputRef.current.value = '';
      return;
    }

    setUploading(true);
    // ⚠️ Прогресс сбрасываем в 0 ЯВНО: иначе при повторной загрузке
    //    после ошибки на экране остались бы прежние 100 %.
    setProgress(0);
    try {
      const res = await uploadsAPI.uploadLayout(file, setProgress);
      // 🔑 Снимок, а не файл: именно его ждёт `orderController.js`.
      onChange(res.data?.data || null);
      // Файл приложен — «пришлю позже» больше не имеет смысла.
      onPendingChange(false);
    } catch (err) {
      // ⚠️ Текст берём от СЕРВЕРА: он различает «слишком большой» (413),
      //    «недопустимый тип» (415) и прочее. Своё сообщение было бы
      //    беднее и могло бы противоречить настоящей причине.
      setError(
        err.response?.data?.message
          || 'Не удалось загрузить файл. Попробуйте ещё раз.'
      );
      onChange(null);
    } finally {
      // ⚠️ `finally`, а не «после успеха»: при ошибке загрузка тоже
      //    обязана закончиться, иначе блок навсегда останется в
      //    состоянии «идёт загрузка» и форма станет неотправляемой.
      setUploading(false);
      // Позволяет выбрать ТОТ ЖЕ файл повторно: без сброса `value`
      // браузер не вызовет `onChange` для того же имени.
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const handleDelete = async () => {
    const filename = layout?.path?.split('/').pop();
    onChange(null);
    onPendingChange(false);
    if (!filename) return;
    try {
      await uploadsAPI.deleteLayout(filename);
    } catch (err) {
      // ⚠️ Не тревожим клиента: файл на диске — не его забота, а заказ
      //    на него уже не ссылается. Пишем в консоль для диагностики.
      console.error('Не удалось удалить файл макета:', err);
    }
  };

  return (
    <div style={{
      marginTop: '12px',
      padding: '12px',
      backgroundColor: 'rgb(var(--c-surface-2))',
      border: '1px solid rgb(var(--c-border))',
      borderRadius: '10px',
    }}>
      <div style={{
        fontSize: '13px',
        fontWeight: 600,
        color: 'rgb(var(--c-text))',
        marginBottom: '8px',
      }}>
        Макет для этой позиции
      </div>

      {layout ? (
        // ── Файл загружен ──────────────────────────────────────────────
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          fontSize: '13px',
        }}>
          <span style={{ color: 'rgb(var(--c-text))', wordBreak: 'break-all' }}>
            📎 {layout.original_name}
            <span style={{ color: 'rgb(var(--c-text-secondary))' }}>
              {' '}({formatSize(layout.size)})
            </span>
          </span>
          <button
            type="button"
            onClick={handleDelete}
            style={{
              background: 'none',
              border: 'none',
              color: '#DC2626',
              cursor: 'pointer',
              fontSize: '13px',
              flexShrink: 0,
              padding: '4px',
            }}
          >
            Удалить
          </button>
        </div>
      ) : (
        // ── Файла нет ──────────────────────────────────────────────────
        <>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            // ⚠️ `display: none` + своя кнопка: системный `input` выглядит
            //    по-разному в браузерах и ломает сетку формы.
            style={{ display: 'none' }}
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <button
            type="button"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
            style={{
              padding: '8px 14px',
              fontSize: '13px',
              borderRadius: '8px',
              border: '1px solid rgb(var(--c-border))',
              backgroundColor: 'rgb(var(--c-surface))',
              color: 'rgb(var(--c-text))',
              cursor: uploading ? 'wait' : 'pointer',
              // ⚠️ Во время загрузки выключаем и галочку ниже: иначе клиент
              //    поставит «пришлю позже», пока файл ещё летит, и получит
              //    ДВА противоречащих сигнала в одном заказе.
              opacity: uploading ? 0.6 : 1,
            }}
          >
            {uploading ? `Загрузка… ${progress}%` : 'Приложить файл макета'}
          </button>

          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            marginTop: '10px',
            fontSize: '13px',
            color: 'rgb(var(--c-text-secondary))',
            lineHeight: 1.4,
          }}>
            <input
              type="checkbox"
              checked={pending}
              disabled={uploading}
              onChange={(e) => onPendingChange(e.target.checked)}
              style={{ marginTop: '2px', flexShrink: 0 }}
            />
            <span>
              Макета нет — пришлю позже. Мы свяжемся и поможем подготовить.
            </span>
          </div>

          <div style={{
            fontSize: '12px',
            color: 'rgb(var(--c-text-secondary))',
            marginTop: '8px',
          }}>
            PDF, AI, EPS, CDR, PSD, JPG, PNG, TIFF, ZIP, WebP · до 20 МБ.
            Можно не прикладывать: макет примет менеджер.
          </div>
        </>
      )}

      {error && (
        <div style={{
          color: '#DC2626',
          fontSize: '13px',
          marginTop: '8px',
          lineHeight: 1.4,
        }}>
          {error}
        </div>
      )}
    </div>
  );
};
